import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { 
  FileText, 
  Search, 
  Eye,
  Edit,
  Trash2,
  Mail,
  Printer,
  ShoppingCart,
  RefreshCw,
  Loader2,
  Plus,
  Minus,
  X,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Ban,
  Undo2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QuotationReceiptModal } from "@/components/QuotationReceiptModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const QuotationManagement = () => {
  const navigate = useNavigate();
  
  // State
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  
  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  
  // Edit state
  const [editNotes, setEditNotes] = useState("");
  const [editValidDays, setEditValidDays] = useState(30);
  const [editItems, setEditItems] = useState([]);
  const [editProductSearch, setEditProductSearch] = useState("");
  const [editProductResults, setEditProductResults] = useState([]);
  const [editSearching, setEditSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Email state
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Convert state
  const [convertPaymentMethod, setConvertPaymentMethod] = useState("CASH");
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quotations?limit=1000`);
      // Sort by date descending (newest first)
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setQuotations(sorted);
    } catch (error) {
      console.error("Error fetching quotations:", error);
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'ACCEPTED':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>;
      case 'CONVERTED':
        return <Badge className="bg-blue-100 text-blue-700"><ShoppingCart className="h-3 w-3 mr-1" />Converted</Badge>;
      case 'SOLD':
        return <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300"><CheckCircle className="h-3 w-3 mr-1" />Sold</Badge>;
      case 'REJECTED':
        return <Badge className="bg-orange-100 text-orange-700 border border-orange-300"><Ban className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      case 'EXPIRED':
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredQuotations = quotations.filter(q => {
    // Status filter
    if (statusFilter !== "ALL" && q.status?.toUpperCase() !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (q.quotation_number || q.quote_number || "").toLowerCase().includes(query) ||
        (q.customer_name || "").toLowerCase().includes(query) ||
        (q.customer_account_number || "").toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const handleView = async (quotation) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotation.id}`);
      setSelectedQuotation(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error fetching quotation details:", error);
      toast.error("Failed to load quotation details");
    }
  };

  const handleEdit = async (quotation) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotation.id}`);
      const fullQuotation = response.data;
      setSelectedQuotation(fullQuotation);
      setEditNotes(fullQuotation.notes || "");
      setEditValidDays(fullQuotation.valid_days || 30);
      setEditItems((fullQuotation.items || []).map((item, i) => ({ ...item, _key: `existing-${i}` })));
      setEditProductSearch("");
      setEditProductResults([]);
      setEditDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load quotation for editing");
    }
  };

  const handleEditProductSearch = async (query) => {
    setEditProductSearch(query);
    if (query.length < 2) { setEditProductResults([]); return; }
    setEditSearching(true);
    try {
      const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=10`);
      setEditProductResults(response.data || []);
    } catch { setEditProductResults([]); }
    finally { setEditSearching(false); }
  };

  const handleAddEditItem = (product) => {
    const existing = editItems.find(i => i.product_id === product.id);
    if (existing) {
      setEditItems(prev => prev.map(i => 
        i.product_id === product.id 
          ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price * (1 - (i.discount_percent || 0) / 100) }
          : i
      ));
    } else {
      const price = product.selling_price_incl_vat || product.selling_price || 0;
      setEditItems(prev => [...prev, {
        _key: `new-${Date.now()}`,
        product_id: product.id,
        plu_code: product.plu_code ? String(product.plu_code) : null,
        name: product.name,
        quantity: 1,
        unit_price: price,
        discount_percent: 0,
        total: price,
        cost_price: product.cost_price || 0,
        is_service: false,
      }]);
    }
    setEditProductSearch("");
    setEditProductResults([]);
  };

  const handleEditItemQty = (index, delta) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newQty = Math.max(0.5, item.quantity + delta);
      return { ...item, quantity: newQty, total: newQty * item.unit_price * (1 - (item.discount_percent || 0) / 100) };
    }));
  };

  const handleEditItemPrice = (index, newPrice) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const price = parseFloat(newPrice) || 0;
      return { ...item, unit_price: price, total: item.quantity * price * (1 - (item.discount_percent || 0) / 100) };
    }));
  };

  const handleRemoveEditItem = (index) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  const getEditTotals = () => {
    const total = editItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const subtotal = total / 1.15;
    const tax = total - subtotal;
    const discount = editItems.reduce((sum, item) => {
      return sum + (item.unit_price * item.quantity * (item.discount_percent || 0)) / 100;
    }, 0);
    return { subtotal, tax, total, discount };
  };

  const handleSaveEdit = async () => {
    if (editItems.length === 0) {
      toast.error("Quotation must have at least one item");
      return;
    }
    setSaving(true);
    try {
      const totals = getEditTotals();
      const payload = {
        notes: editNotes,
        valid_days: editValidDays,
        items: editItems.map(({ _key, ...item }) => item),
        subtotal: Math.round(totals.subtotal * 100) / 100,
        tax: Math.round(totals.tax * 100) / 100,
        total: Math.round(totals.total * 100) / 100,
        discount: Math.round(totals.discount * 100) / 100,
      };
      await saveWithRetry(() => axios.put(`${API}/quotations/${selectedQuotation.id}`, payload));
      toast.success("Quotation updated");
      setEditDialogOpen(false);
      fetchQuotations();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to update quotation"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (quotation, newStatus) => {
    try {
      await saveWithRetry(() => axios.put(`${API}/quotations/${quotation.id}/status?status=${newStatus}`));
      toast.success(`Quotation ${newStatus.toLowerCase()}`);
      fetchQuotations();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(errorMessage(error, "Failed to update status"));
    }
  };

  const handlePrint = (quotation) => {
    setSelectedQuotation(quotation);
    setPrintDialogOpen(true);
  };

  const handleEmailOpen = (quotation) => {
    setSelectedQuotation(quotation);
    setEmailAddress(quotation.customer_email || "");
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/quotation`, {
        quotation_id: selectedQuotation.id,
        recipient_email: emailAddress
      });
      toast.success(`Quotation emailed to ${emailAddress}`);
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleConvertOpen = (quotation) => {
    setSelectedQuotation(quotation);
    setConvertPaymentMethod("CASH");
    setConvertDialogOpen(true);
  };

  const handleConvertToSale = async () => {
    setConverting(true);
    try {
      const response = await axios.post(
        `${API}/quotations/${selectedQuotation.id}/convert-to-sale?payment_method=${convertPaymentMethod}`
      );
      const sale = response.data;
      toast.success(`Quotation converted to sale: ${sale.transaction_number}`);
      setConvertDialogOpen(false);
      fetchQuotations();

      // Auto-email invoice to the customer — mirrors CheckoutModal behaviour
      // so quote-driven sales don't silently skip the email step.
      await autoEmailInvoice(sale);
    } catch (error) {
      console.error("Error converting quotation:", error);
      toast.error(error.response?.data?.detail || "Failed to convert quotation");
    } finally {
      setConverting(false);
    }
  };

  // Send the invoice email to the customer of the converted sale, respecting:
  //   • the global emails_enabled setting
  //   • the customer's own auto_email opt-out flag
  //   • whether the customer has an email at all
  const autoEmailInvoice = async (sale) => {
    if (!sale?.id) return;
    // Global email switch
    try {
      const settingsResponse = await axios.get(`${API}/settings/email`);
      if (!settingsResponse.data?.emails_enabled) return;
    } catch (e) { /* proceed on error, backend will still gate if disabled */ }

    // Fetch customer for email + preferences
    let customerEmail = sale.customer_email || null;
    let autoEmail = true;
    let customerName = sale.customer_name || "Customer";
    if (sale.customer_id) {
      try {
        const cust = await axios.get(`${API}/customers/${sale.customer_id}`);
        customerEmail = customerEmail || cust.data?.email;
        autoEmail = cust.data?.auto_email !== false;
        customerName = cust.data?.name || customerName;
      } catch (e) { /* ignore, will fall back to prompt/skip */ }
    }
    if (!autoEmail) {
      toast.info(`Auto-email disabled for ${customerName} — invoice not sent`);
      return;
    }
    if (!customerEmail) {
      toast.warning(`No email on file for ${customerName} — invoice not sent. Add an email on the customer's profile and re-send from the Sales page.`);
      return;
    }
    try {
      const res = await axios.post(`${API}/email/sale-invoice`, {
        sale_id: sale.id,
        recipient_email: customerEmail,
      });
      if (res.data?.blocked_reason === "emails_disabled_in_settings") return;
      toast.success(`Invoice emailed to ${customerEmail}`);
    } catch (e) {
      if (!e.response?.data?.blocked_reason) {
        toast.error(`Failed to email invoice to ${customerEmail}`);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (quotation) => {
    const expiryDate = quotation.expires_at || quotation.valid_until;
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">Quotation Management</h1>
            <p className="text-gray-500">View, edit, and manage customer quotations</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchQuotations}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => navigate("/quotations")} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-2" />
            New Quotation
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by quote number, customer name, or account..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="ACCEPTED">Accepted</SelectItem>
                  <SelectItem value="CONVERTED">Converted to Sale</SelectItem>
                  <SelectItem value="SOLD">Sold</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Quotations ({filteredQuotations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600" />
              <p className="text-gray-500 mt-2">Loading quotations...</p>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No quotations found</p>
              <Button 
                onClick={() => navigate("/quotations")} 
                className="mt-4 bg-purple-600 hover:bg-purple-700"
              >
                Create First Quotation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotations.map((quotation) => (
                  <TableRow key={quotation.id} className={isExpired(quotation) && quotation.status === "PENDING" ? "bg-red-50" : ""}>
                    <TableCell className="font-mono font-semibold text-purple-600">
                      {quotation.quotation_number || quotation.quote_number || "-"}
                    </TableCell>
                    <TableCell>{formatDate(quotation.created_at)}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{quotation.customer_name || "Walk-in"}</div>
                        {quotation.customer_account_number && (
                          <div className="text-xs text-gray-500">{quotation.customer_account_number}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{quotation.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-semibold">
                      R {(quotation.total || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {(quotation.expires_at || quotation.valid_until) ? (
                        <span className={isExpired(quotation) ? "text-red-600" : ""}>
                          {formatDate(quotation.expires_at || quotation.valid_until)}
                          {isExpired(quotation) && <span className="text-xs ml-1">(Expired)</span>}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>{getStatusBadge(quotation.status)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(quotation)} title="View" data-testid={`view-quotation-${quotation.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {quotation.status === "PENDING" && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(quotation)} title="Edit" data-testid={`edit-quotation-${quotation.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handlePrint(quotation)} title="Print" data-testid={`print-quotation-${quotation.id}`}>
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEmailOpen(quotation)} 
                          title="Email Quote"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          data-testid={`email-quotation-${quotation.id}`}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Email
                        </Button>
                        {quotation.status === "PENDING" && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => navigate("/sales", { state: { loadQuotationId: quotation.id } })}
                              title="Load into the till cart (sale is not completed)"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              data-testid={`convert-quotation-${quotation.id}`}
                            >
                              <ShoppingCart className="h-4 w-4 mr-1" />
                              Sell
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateStatus(quotation, "SOLD")}
                              title="Mark as Sold (no POS sale created)"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`mark-sold-quotation-${quotation.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Sold
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateStatus(quotation, "REJECTED")}
                              title="Mark as Rejected (customer said no)"
                              className="text-orange-700 border-orange-300 hover:bg-orange-50"
                              data-testid={`mark-rejected-quotation-${quotation.id}`}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleUpdateStatus(quotation, "CANCELLED")}
                              title="Cancel"
                              className="text-red-600"
                              data-testid={`cancel-quotation-${quotation.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(quotation.status === "SOLD" || quotation.status === "REJECTED") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdateStatus(quotation, "PENDING")}
                            title="Undo — revert to Pending"
                            className="text-gray-600 hover:text-gray-900"
                            data-testid={`undo-status-quotation-${quotation.id}`}
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            Undo
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quotation Details - {selectedQuotation?.quotation_number || selectedQuotation?.quote_number}</DialogTitle>
          </DialogHeader>
          {selectedQuotation && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Customer</Label>
                  <div className="font-semibold">{selectedQuotation.customer_name || "Walk-in"}</div>
                  {selectedQuotation.customer_account_number && (
                    <div className="text-sm text-gray-600">{selectedQuotation.customer_account_number}</div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div>{getStatusBadge(selectedQuotation.status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Created</Label>
                  <div>{formatDate(selectedQuotation.created_at)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Valid Until</Label>
                  <div className={isExpired(selectedQuotation) ? "text-red-600" : ""}>
                    {formatDate(selectedQuotation.expires_at || selectedQuotation.valid_until)}
                  </div>
                </div>
              </div>

              {/* Items */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PLU</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedQuotation.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono text-sm">{item.plu_code}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">R {(item.unit_price || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right font-semibold">R {(item.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal (excl VAT):</span>
                  <span>R {(selectedQuotation.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (15%):</span>
                  <span>R {(selectedQuotation.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-purple-600">R {(selectedQuotation.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedQuotation.notes && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Notes</Label>
                  <p className="text-sm">{selectedQuotation.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            {selectedQuotation?.status === "PENDING" && (
              <Button onClick={() => { setViewDialogOpen(false); navigate("/sales", { state: { loadQuotationId: selectedQuotation.id } }); }} className="bg-green-600 hover:bg-green-700" data-testid="view-quotation-sell-btn">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Sell (load to cart)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Quotation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quotation</Label>
              <div className="font-semibold">{selectedQuotation?.quotation_number || selectedQuotation?.quote_number}</div>
              <div className="text-sm text-gray-500">{selectedQuotation?.customer_name}</div>
            </div>
            <div>
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="customer@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-purple-600 hover:bg-purple-700">
              {sendingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - Full Item Editing */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Quotation — {selectedQuotation?.quotation_number || selectedQuotation?.quote_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer info summary */}
            <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <div className="font-semibold">{selectedQuotation?.customer_name || "Walk-in"}</div>
                {selectedQuotation?.customer_account_number && (
                  <div className="text-xs text-gray-500">{selectedQuotation.customer_account_number}</div>
                )}
              </div>
            </div>

            {/* Product Search */}
            <div className="relative">
              <Label className="text-sm font-medium">Add Products</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search product name, PLU code, or barcode..."
                  value={editProductSearch}
                  onChange={(e) => handleEditProductSearch(e.target.value)}
                  className="pl-10"
                  data-testid="edit-quote-product-search"
                />
              </div>
              {editProductResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {editProductResults.map((product) => (
                    <button
                      key={product.id}
                      className="w-full text-left px-3 py-2 hover:bg-purple-50 flex justify-between items-center border-b last:border-0"
                      onClick={() => handleAddEditItem(product)}
                      data-testid={`edit-quote-add-product-${product.id}`}
                    >
                      <div>
                        <div className="font-medium text-sm">{product.name}</div>
                        <div className="text-xs text-gray-500">{product.plu_code}</div>
                      </div>
                      <div className="text-sm font-semibold text-purple-600">
                        R{(product.selling_price_incl_vat || product.selling_price || 0).toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {editSearching && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-gray-500 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Searching...
                </div>
              )}
            </div>

            {/* Items Table */}
            {editItems.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg text-gray-500 text-sm">
                No items. Search and add products above.
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-center w-32">Qty</TableHead>
                      <TableHead className="text-right w-28">Price</TableHead>
                      <TableHead className="text-right w-28">Total</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editItems.map((item, index) => (
                      <TableRow key={item._key || index}>
                        <TableCell className="text-gray-400 text-xs">{index + 1}</TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{item.name}</div>
                          {item.plu_code && <div className="text-xs text-gray-400">{item.plu_code}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleEditItemQty(index, -1)}
                              data-testid={`edit-quote-qty-minus-${index}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-10 text-center font-medium text-sm">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleEditItemQty(index, 1)}
                              data-testid={`edit-quote-qty-plus-${index}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => handleEditItemPrice(index, e.target.value)}
                            className="w-24 text-right h-8 text-sm ml-auto"
                            step="0.01"
                            min="0"
                            data-testid={`edit-quote-price-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold text-sm">
                          R{(item.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveEditItem(index)}
                            data-testid={`edit-quote-remove-${index}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Totals */}
            {editItems.length > 0 && (() => {
              const totals = getEditTotals();
              return (
                <div className="border-t pt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal (excl VAT):</span>
                    <span>R{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>VAT (15%):</span>
                    <span>R{totals.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-purple-600">R{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Notes & Valid Days */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid Days</Label>
                <Input
                  type="number"
                  value={editValidDays}
                  onChange={(e) => setEditValidDays(parseInt(e.target.value) || 30)}
                  min="1"
                  max="365"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Notes..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving || editItems.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="edit-quote-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Sale Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Quotation to Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold">{selectedQuotation?.quotation_number || selectedQuotation?.quote_number}</div>
              <div className="text-sm text-gray-500">{selectedQuotation?.customer_name}</div>
              <div className="text-lg font-bold text-purple-600 mt-2">
                Total: R {(selectedQuotation?.total || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={convertPaymentMethod} onValueChange={setConvertPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CARD">Card</SelectItem>
                  <SelectItem value="EFT">EFT</SelectItem>
                  <SelectItem value="ACCOUNT">Account</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-gray-500">
              This will create a new sale transaction and mark the quotation as converted.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToSale} disabled={converting} className="bg-green-600 hover:bg-green-700">
              {converting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
              Convert to Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <QuotationReceiptModal
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        quotation={selectedQuotation}
      />
    </div>
  );
};

export default QuotationManagement;
